import { useMemo, useState } from 'react';
import { BarChart3, Calculator, Download, FileInput, Globe2, Leaf, MapPinned, Radar, ShieldCheck, TriangleAlert } from 'lucide-react';
import { MetricCard } from './components/MetricCard';
import { ScenarioChart } from './components/ScenarioChart';
import { MapView } from './components/MapView';
import { defaultScenario, sectorCatalog } from './data/defaultScenario';
import { calculateScenario } from './lib/calculations';
import { readCsvSummary, readGeoSummary, readRasterSummary } from './lib/fileReaders';
import { ActiveTab, CalculationResult, GeoSummary, RasterSummary, ScenarioInput, SectorId } from './types';
import { FeatureCollection } from 'geojson';

const money = (value: number) =>
  new Intl.NumberFormat('zh-CN', {
    maximumFractionDigits: 2,
  }).format(value);

const number = (value: number, digits = 2) =>
  new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);

const tabs: Array<{ id: ActiveTab; label: string; icon: JSX.Element }> = [
  { id: 'calculator', label: '计算器', icon: <Calculator size={16} /> },
  { id: 'gis', label: 'GIS', icon: <MapPinned size={16} /> },
  { id: 'rs', label: '遥感', icon: <Radar size={16} /> },
  { id: 'finance', label: '财务', icon: <BarChart3 size={16} /> },
  { id: 'export', label: '导出', icon: <Download size={16} /> },
];

export default function App() {
  const [scenario, setScenario] = useState<ScenarioInput>(defaultScenario);
  const [activeTab, setActiveTab] = useState<ActiveTab>('calculator');
  const [geojson, setGeojson] = useState<FeatureCollection | null>(null);
  const [geoSummary, setGeoSummary] = useState<GeoSummary | null>(null);
  const [rasterSummary, setRasterSummary] = useState<RasterSummary | null>(null);
  const [csvSummary, setCsvSummary] = useState<{ rows: number; columns: string[]; preview: Array<Record<string, string>> } | null>(null);

  const result: CalculationResult = useMemo(() => {
    const area = geoSummary?.areaHa ?? 0;
    const ndvi = rasterSummary?.ndviMean ?? 0.45;
    return calculateScenario(scenario, area, ndvi);
  }, [scenario, geoSummary?.areaHa, rasterSummary?.ndviMean]);

  const sectorMeta = sectorCatalog.find((item) => item.id === scenario.sector) ?? sectorCatalog[0];

  const update = <K extends keyof ScenarioInput>(key: K, value: ScenarioInput[K]) => {
    setScenario((current) => ({ ...current, [key]: value }));
  };

  const onGeojsonUpload = async (file: File) => {
    const { summary, geojson: data } = await readGeoSummary(file);
    setGeojson(data);
    setGeoSummary(summary);
  };

  const onRasterUpload = async (file: File) => {
    const summary = await readRasterSummary(file);
    setRasterSummary(summary);
  };

  const onCsvUpload = async (file: File) => {
    const summary = await readCsvSummary(file);
    setCsvSummary(summary);
  };

  const downloadJson = () => {
    const payload = {
      scenario,
      geoSummary,
      rasterSummary,
      csvSummary,
      result,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${scenario.projectName || 'ccer-scenario'}.json`);
  };

  const downloadCsv = () => {
    const rows = [
      ['year', 'gross', 'net', 'revenue', 'cashFlow', 'discountedCashFlow'],
      ...result.annualSeries.map((row) => [
        String(row.year),
        String(row.gross),
        String(row.net),
        String(row.revenue),
        String(row.cashFlow),
        String(row.discountedCashFlow),
      ]),
    ];
    const csv = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${scenario.projectName || 'ccer-scenario'}-annual-series.csv`);
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <div className="eyebrow">CCER calculator</div>
          <h1>CCER 计算器</h1>
          <p>面向多行业项目的 CCER 估算、GIS 边界核验、遥感辅助判断和财务测算。</p>
        </div>
        <div className="status-row">
          <div className="status-chip"><ShieldCheck size={14} /> 静态可部署</div>
          <div className="status-chip"><Globe2 size={14} /> Cloudflare Pages</div>
          <div className="status-chip"><Leaf size={14} /> GIS + RS</div>
        </div>
      </header>

      <div className="workspace">
        <aside className="sidebar">
          <section className="panel">
            <div className="panel-title">项目概况</div>
            <label className="field">
              <span>项目名称</span>
              <input value={scenario.projectName} onChange={(e) => update('projectName', e.target.value)} />
            </label>
            <label className="field">
              <span>区域</span>
              <input value={scenario.region} onChange={(e) => update('region', e.target.value)} />
            </label>
            <label className="field">
              <span>行业</span>
              <select value={scenario.sector} onChange={(e) => update('sector', e.target.value as SectorId)}>
                {sectorCatalog.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="sector-note">
              <strong>{sectorMeta.label}</strong>
              <p>{sectorMeta.summary}</p>
              <span>{sectorMeta.hint}</span>
            </div>
          </section>

          <section className="panel">
            <div className="panel-title">核心参数</div>
            <div className="grid-two">
              <NumberField label="面积(ha)" value={scenario.areaHa} onChange={(v) => update('areaHa', v)} />
              <NumberField label="期限(年)" value={scenario.durationYears} onChange={(v) => update('durationYears', v)} />
              <NumberField label="起始年" value={scenario.startYear} onChange={(v) => update('startYear', v)} />
              <NumberField label="碳价" value={scenario.creditPrice} onChange={(v) => update('creditPrice', v)} />
              <NumberField label="折现率" value={scenario.discountRate} step={0.01} onChange={(v) => update('discountRate', v)} />
              <NumberField label="资本开支" value={scenario.capitalCost} onChange={(v) => update('capitalCost', v)} />
              <NumberField label="年度运维" value={scenario.opexAnnual} onChange={(v) => update('opexAnnual', v)} />
              <NumberField label="缓冲池" value={scenario.bufferShare} step={0.01} onChange={(v) => update('bufferShare', v)} />
              <NumberField label="泄漏率" value={scenario.leakageRate} step={0.01} onChange={(v) => update('leakageRate', v)} />
              <NumberField label="永久性" value={scenario.permanenceRate} step={0.01} onChange={(v) => update('permanenceRate', v)} />
            </div>
          </section>

          <section className="panel">
            <div className="panel-title">行业参数</div>
            <div className="grid-two">
              <NumberField label="树密度" value={scenario.treeDensity} onChange={(v) => update('treeDensity', v)} />
              <NumberField label="生物量增量" value={scenario.biomassIncrement} step={0.1} onChange={(v) => update('biomassIncrement', v)} />
              <NumberField label="土壤固碳" value={scenario.soilSequestration} step={0.1} onChange={(v) => update('soilSequestration', v)} />
              <NumberField label="产量变化" value={scenario.yieldDelta} step={0.01} onChange={(v) => update('yieldDelta', v)} />
              <NumberField label="秸秆系数" value={scenario.residueFactor} step={0.1} onChange={(v) => update('residueFactor', v)} />
              <NumberField label="电网因子" value={scenario.gridFactor} step={0.01} onChange={(v) => update('gridFactor', v)} />
              <NumberField label="节电量" value={scenario.energySavedMWh} onChange={(v) => update('energySavedMWh', v)} />
              <NumberField label="系统效率" value={scenario.systemEfficiency} step={0.01} onChange={(v) => update('systemEfficiency', v)} />
              <NumberField label="甲烷减排" value={scenario.methaneAbatementTco2e} onChange={(v) => update('methaneAbatementTco2e', v)} />
              <NumberField label="水体负荷" value={scenario.wastewaterLoad} step={0.1} onChange={(v) => update('wastewaterLoad', v)} />
              <NumberField label="水文指数" value={scenario.waterIndex} step={0.01} onChange={(v) => update('waterIndex', v)} />
              <NumberField label="湿地占比" value={scenario.wetlandAreaShare} step={0.01} onChange={(v) => update('wetlandAreaShare', v)} />
            </div>
          </section>

          <section className="panel">
            <div className="panel-title">数据导入</div>
            <UploadButton label="上传 GeoJSON" accept=".json,.geojson" icon={<FileInput size={16} />} onFile={onGeojsonUpload} />
            <UploadButton label="上传 GeoTIFF" accept=".tif,.tiff" icon={<Radar size={16} />} onFile={onRasterUpload} />
            <UploadButton label="上传 CSV" accept=".csv" icon={<FileInput size={16} />} onFile={onCsvUpload} />
          </section>
        </aside>

        <main className="main">
          <nav className="tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={tab.id === activeTab ? 'tab active' : 'tab'}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          {activeTab === 'calculator' ? (
            <section className="panel large">
              <div className="panel-title">计算结果</div>
              <div className="metrics-grid">
                <MetricCard label="总减排" value={`${number(result.netCredits)} tCO2e`} hint="保守后净额" icon={<Leaf size={16} />} tone="green" />
                <MetricCard label="毛减排" value={`${number(result.grossCredits)} tCO2e`} hint="未折减前" icon={<Calculator size={16} />} tone="blue" />
                <MetricCard label="NPV" value={`¥ ${money(result.npv)}`} hint="按年度现金流折现" icon={<BarChart3 size={16} />} tone="amber" />
                <MetricCard label="质量分" value={`${number(result.qualityScore, 0)}/100`} hint="数据完整度 + GIS + RS" icon={<ShieldCheck size={16} />} tone="green" />
                <MetricCard label="风险分" value={`${number(result.riskScore, 0)}/100`} hint="分值越高风险越大" icon={<TriangleAlert size={16} />} tone="red" />
                <MetricCard label="回收期" value={result.paybackYears ? `${number(result.paybackYears, 1)} 年` : '未回收'} hint="现金流累计回正" icon={<Download size={16} />} tone="neutral" />
              </div>

              <div className="content-grid">
                <div className="stack">
                  <div className="subpanel">
                    <div className="subpanel-title">年度曲线</div>
                    <ScenarioChart result={result} />
                  </div>
                </div>
                <div className="stack">
                  <div className="subpanel">
                    <div className="subpanel-title">项目判断</div>
                    <ul className="bullet-list">
                      {result.findings.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {activeTab === 'gis' ? (
            <section className="panel large">
              <div className="panel-title">GIS 边界</div>
              <div className="content-grid map-grid">
                <div className="map-column">
                  <MapView geojson={geojson} centroid={geoSummary?.centroid ?? null} />
                </div>
                <div className="stack">
                  <div className="subpanel">
                    <div className="subpanel-title">边界摘要</div>
                    {geoSummary ? (
                      <div className="summary-list">
                        <SummaryRow label="来源" value={geoSummary.sourceName} />
                        <SummaryRow label="图斑数" value={String(geoSummary.featureCount)} />
                        <SummaryRow label="几何类型" value={geoSummary.geometryType} />
                        <SummaryRow label="面积" value={`${number(geoSummary.areaHa)} ha`} />
                        <SummaryRow label="点要素" value={String(geoSummary.pointCount)} />
                        <SummaryRow
                          label="中心点"
                          value={geoSummary.centroid ? `${number(geoSummary.centroid[0], 5)}, ${number(geoSummary.centroid[1], 5)}` : '-'}
                        />
                      </div>
                    ) : (
                      <p className="muted">上传 GeoJSON 后这里会显示边界、面积和中心点。</p>
                    )}
                  </div>
                  <div className="subpanel">
                    <div className="subpanel-title">方法提示</div>
                    <p className="muted">
                      这里适合承载地块范围核验、缓冲区分析、生态红线比对、采样点定位和坐标一致性检查。
                    </p>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {activeTab === 'rs' ? (
            <section className="panel large">
              <div className="panel-title">遥感分析</div>
              <div className="content-grid rs-grid">
                <div className="stack">
                  <div className="subpanel">
                    <div className="subpanel-title">影像预览</div>
                    {rasterSummary?.previewDataUrl ? (
                      <img className="raster-preview" src={rasterSummary.previewDataUrl} alt="raster preview" />
                    ) : (
                      <div className="placeholder">上传 GeoTIFF 后显示预览图。</div>
                    )}
                  </div>
                </div>
                <div className="stack">
                  <div className="subpanel">
                    <div className="subpanel-title">波段统计</div>
                    {rasterSummary ? (
                      <div className="summary-list">
                        <SummaryRow label="来源" value={rasterSummary.sourceName} />
                        <SummaryRow label="分辨率" value={`${rasterSummary.width} × ${rasterSummary.height}`} />
                        <SummaryRow label="波段数" value={String(rasterSummary.bandCount)} />
                        <SummaryRow label="均值" value={number(rasterSummary.mean, 4)} />
                        <SummaryRow label="标准差" value={number(rasterSummary.stdDev, 4)} />
                        <SummaryRow label="NDVI 均值" value={rasterSummary.ndviMean === null ? '-' : number(rasterSummary.ndviMean, 4)} />
                        <SummaryRow label="高植被覆盖" value={rasterSummary.ndviCoverage === null ? '-' : `${number(rasterSummary.ndviCoverage * 100, 1)}%`} />
                      </div>
                    ) : (
                      <p className="muted">上传 GeoTIFF 后会计算统计值、NDVI 和覆盖比例。</p>
                    )}
                  </div>
                  <div className="subpanel">
                    <div className="subpanel-title">CSV 采样</div>
                    {csvSummary ? (
                      <div className="summary-list">
                        <SummaryRow label="行数" value={String(csvSummary.rows)} />
                        <SummaryRow label="字段" value={csvSummary.columns.join(' / ') || '-'} />
                      </div>
                    ) : (
                      <p className="muted">可上传样点 CSV，方便叠加地面核查和字段汇总。</p>
                    )}
                    {csvSummary?.preview.length ? (
                      <div className="csv-preview">
                        {csvSummary.preview.map((row, index) => (
                          <pre key={index}>{JSON.stringify(row, null, 2)}</pre>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {activeTab === 'finance' ? (
            <section className="panel large">
              <div className="panel-title">财务分析</div>
              <div className="metrics-grid">
                <MetricCard label="年度收入" value={`¥ ${money(result.grossRevenue)}`} hint="全周期销售收入汇总" icon={<BarChart3 size={16} />} />
                <MetricCard label="年度现金流" value={`¥ ${money(result.netCashFlow)}`} hint="未折现总现金流" icon={<Calculator size={16} />} />
                <MetricCard label="单位收益" value={`¥ ${money(result.netCredits * scenario.creditPrice / Math.max(1, scenario.areaHa))}/ha`} hint="按项目净额估算" icon={<Leaf size={16} />} />
                <MetricCard label="边际信号" value={`${number(result.riskScore, 0)}/100`} hint="更多用于项目筛选" icon={<TriangleAlert size={16} />} />
              </div>
              <div className="subpanel">
                <div className="subpanel-title">年度现金流</div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>年份</th>
                      <th>毛减排</th>
                      <th>净减排</th>
                      <th>收入</th>
                      <th>现金流</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.annualSeries.map((row) => (
                      <tr key={row.year}>
                        <td>{row.year}</td>
                        <td>{number(row.gross)}</td>
                        <td>{number(row.net)}</td>
                        <td>{money(row.revenue)}</td>
                        <td>{money(row.cashFlow)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {activeTab === 'export' ? (
            <section className="panel large">
              <div className="panel-title">导出</div>
              <div className="action-row">
                <button className="action-button" onClick={downloadJson}>
                  <Download size={16} />
                  导出 JSON
                </button>
                <button className="action-button" onClick={downloadCsv}>
                  <Download size={16} />
                  导出年度 CSV
                </button>
              </div>
              <div className="subpanel">
                <div className="subpanel-title">打包内容</div>
                <p className="muted">
                  这个项目已经按 Cloudflare Pages 的静态站点结构整理好了，构建后只需要把 `dist`
                  上传或连接仓库即可。
                </p>
                <ul className="bullet-list">
                  <li>前端源代码：`src/`</li>
                  <li>构建配置：`vite.config.ts`、`tsconfig*.json`</li>
                  <li>Cloudflare 配置：`wrangler.toml`</li>
                  <li>部署说明：`README.md`</li>
                </ul>
              </div>
            </section>
          ) : null}
        </main>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="field compact">
      <span>{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => {
          const parsed = Number(e.target.value);
          onChange(Number.isFinite(parsed) ? parsed : 0);
        }}
      />
    </label>
  );
}

function UploadButton({
  label,
  accept,
  icon,
  onFile,
}: {
  label: string;
  accept: string;
  icon: JSX.Element;
  onFile: (file: File) => void | Promise<void>;
}) {
  return (
    <label className="upload">
      {icon}
      <span>{label}</span>
      <input
        type="file"
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onFile(file);
        }}
      />
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="summary-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function triggerDownload(url: string, filename: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
